import { FC } from "react";
import { ICompetition } from "../../interfaces/StockFinancials.interface";
import { TRADING_MALL_TABLE_VALUE_MAP } from "./StockFinancials.constants";
import { TableColumnDetailsCTEntity } from "../../interfaces/ITradingMallContentV1.interface";
import { getStockPdpUrl } from "../News/utils/stockUrlUtils";
import { sortObjectByKeySuffix } from "@repo/shared-utils/common-utils";
import { currencify } from "@repo/shared-utils/num-utils";
import BFLLink from "@repo/shared-ui/BFLLink";
import { getAbsoluteUrl } from "../../utils/helper";

const extractDataKeys = (headerObj: any) => {
  return Object.values(headerObj).map((val: any) => {
    const labelKey = Object.keys(val).find((key) =>
      key.startsWith("tableColumnTitleText"),
    );
    const label = val[labelKey || ""] || "";
    if (label in TRADING_MALL_TABLE_VALUE_MAP) {
      return TRADING_MALL_TABLE_VALUE_MAP[
        label as keyof typeof TRADING_MALL_TABLE_VALUE_MAP
      ];
    }
    return "";
  });
};

interface StockFinancialsCompetitionTableProps {
  stockFinancials: ICompetition[];
  header: TableColumnDetailsCTEntity[];
}

const StockFinancialsCompetitionTable: FC<
  StockFinancialsCompetitionTableProps
> = ({ stockFinancials, header }) => {
  const orderedArray = sortObjectByKeySuffix(
    header?.[0] as unknown as Record<string, unknown>,
    "item",
  ) as TableColumnDetailsCTEntity[];

  const filteredStockFinancials = stockFinancials?.filter((item) => {
    if (!item) return false;

    const hasCompName = Boolean(item.compName?.toString().trim());
    const hasSymbol = Boolean(item.symbol?.toString().trim());

    return hasCompName || hasSymbol;
  });

  const dataKeys = extractDataKeys(orderedArray);
  return (
    <div className="w-full">
      <div className="custom-scroll relative mt-5 overflow-x-auto rounded">
        <table className="w-full min-w-[1000px] border-separate border-spacing-0 border border-gray-200">
          <thead className="bg-lgray-200">
            <tr>
              {orderedArray?.map((label: any, index: number) => (
                <th
                  key={index}
                  className={`border border-gray-200 p-5 text-left text-sm font-medium ${
                    index === 0
                      ? "bg-lgray-200 sticky left-0 z-10 w-[170px] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]"
                      : ""
                  }`}
                >
                  {label?.tableColumnTitleTextCT}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredStockFinancials?.map((item: ICompetition, rowIndex) => (
              <tr
                key={rowIndex}
                className={`text-sm ${rowIndex % 2 === 0 ? "bg-white" : "bg-lgray-100"}`}
              >
                {dataKeys.map((key, colIndex) => {
                  let cellContent = item[key as keyof typeof item];
                  if (key === "wkRange") {
                    cellContent = `${currencify(item.wkLow52, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} / ${currencify(item.wkHigh52, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  } else if (typeof cellContent === "number") {
                    cellContent =
                      key === "price" || key === "marketCap"
                        ? `${currencify(cellContent, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : cellContent;
                  } else if (!cellContent) {
                    cellContent = "-";
                  }

                  // Determine background color based on row index
                  const rowBgColor =
                    rowIndex % 2 === 0 ? "bg-white" : "bg-lgray-100";

                  return (
                    <td
                      key={colIndex}
                      className={`border border-gray-200 p-3 leading-[22px] ${
                        colIndex === 0
                          ? `text-link-primary sticky left-0 z-10 w-[170px] ${rowBgColor} font-medium shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]`
                          : ""
                      } ${colIndex === 2 || colIndex === 3 ? "w-[174px]" : "w-[215px]"}`}
                    >
                      {colIndex === 0 ? (
                        <BFLLink                        
                          href={getAbsoluteUrl(getStockPdpUrl(item?.symbol ?? ""))}
                        >
                          {cellContent}
                        </BFLLink>
                      ) : (
                        cellContent
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockFinancialsCompetitionTable;
